import React, { useEffect, useState } from "react";
import "react-alice-carousel/lib/alice-carousel.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import bookings from "../../../apiConfigs/bookings";
import { ReactComponent as View } from "../../../assets/view.svg";
import DeleteModal from "../../../designComponents/DeleteModal";
import Loading from "../../../designComponents/Loading";
import Table from "../../../designComponents/Table";
import requestAPI from "../../../utils/requestAPI";

import {
  renderFullDetails,
  renderRole,
  renderstatus,
} from "../../../designComponents/Table/renderMethods";
import { humanReadableDateWithTime } from "../../../utils/date";
import {
  ButtonsSection,
  Card,
  LabelText,
  RejectButton,
  SectionTitle,
  Title,
  ValueText,
  Wrapper,
} from "../../Properties/Details/styles";
import { useRoot } from "../../../context/RootProvider";

type Props = {};

type BookingDetailsType = {
  bookingStatus: string;
  landlordId: {
    _id: string;
    avatar: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  propertyId: {
    images: [];
    address: string;
    area: string;
    bathrooms: number;
    bedrooms: number;
    price: number;
    furnishedType: string;
    propertyType: string;
    rentalMarket: string;
    description: string;

    features: [];
    pricing: { priceType: string; price: number };
  };
  tenants?: Tenant[];
};
interface Tenant {
  tenantId: {
    _id: string;

    firstName: string;
    lastName: string;
    tenantId: string;
    avatar: string;
    email: string;
  };
  status: string;
  createdAt: string;
  _id: string;
}

const BookingDetails: React.FC<Props> = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [bookingDetail, setBookingDetail] = useState<BookingDetailsType>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getSingleBookingDetail = async (id: string) => {
    setIsLoading(true);
    try {
      const response = await requestAPI(bookings.getSingleProperty(id));
      setBookingDetail(response);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (bookingId) {
      getSingleBookingDetail(bookingId);
    }
  }, [bookingId]);

  const handleDeleteBooking = async (id: string) => {
    setLoading(true);
    try {
      requestAPI(bookings.deleteBooking(id));
      setLoading(false);
      setIsDeleteModalOpen(false);
      navigate("/bookings");
      toast.success("Booking deleted successfully");
    } catch (error) {
      setLoading(false);
    }
  };

  const tableData = {
    headings: [
      { key: "name", label: "Full Detail" },
      { key: "email", label: "Email Address" },
      { key: "role", label: "Role" },
      { key: "status", label: "Booking Status" },
      { key: "bookingDate", label: "Booking Requested on" },
      { key: "actions", label: "Actions" },
    ],
    body: bookingDetail?.tenants
      ? bookingDetail.tenants.map((tenant) => ({
          id: tenant._id,
          name: renderFullDetails({
            avatar: tenant?.tenantId?.avatar,
            firstName: tenant?.tenantId?.firstName,
            lastName: tenant?.tenantId?.lastName,
            email: tenant?.tenantId?.email,
          }),
          email: tenant?.tenantId?.email,
          role: renderRole("TENANT"),
          status: renderstatus(tenant?.status),
          bookingDate: humanReadableDateWithTime(tenant?.createdAt),
          actions: (
            <div
              onClick={() => navigate(`/users/${tenant?.tenantId?._id}`)}
              style={{
                display: "flex",
                gap: "4px",
                justifyContent: "flex-end",
                cursor: "pointer",
              }}
            >
              <View />
            </div>
          ),
        }))
      : [],
  };
  const { auth } = useRoot();

  return (
    <Wrapper>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <Title>Booking Details</Title>
          <div style={{ marginTop: "10px" }}>
            <ButtonsSection>
              <div
                style={{ display: "flex", gap: "70px", alignItems: "center" }}
              >
                <SectionTitle>
                  {bookingDetail?.propertyId?.address}
                </SectionTitle>
              </div>
              <div>
                {auth?.role === "ADMIN" && (
                  <RejectButton onClick={() => setIsDeleteModalOpen(true)}>
                    Delete Booking
                  </RejectButton>
                )}
              </div>
            </ButtonsSection>
          </div>

          <Card style={{ margin: "0px" }}>
            <ButtonsSection>
              <LabelText>Area</LabelText>
              <ValueText>{bookingDetail?.propertyId?.area} Sq.ft</ValueText>
            </ButtonsSection>
            <ButtonsSection>
              <LabelText>Pricing</LabelText>
              <ValueText>
                € {bookingDetail?.propertyId?.pricing?.price}{" "}
                {bookingDetail?.propertyId?.pricing?.priceType}
              </ValueText>
            </ButtonsSection>
            <ButtonsSection>
              <LabelText>Bedrooms</LabelText>
              <ValueText>{bookingDetail?.propertyId?.bedrooms}</ValueText>
            </ButtonsSection>
            <ButtonsSection>
              <LabelText>Bathrooms</LabelText>
              <ValueText>{bookingDetail?.propertyId?.bathrooms}</ValueText>
            </ButtonsSection>
            <ButtonsSection>
              <LabelText>Property Type</LabelText>
              <ValueText>{bookingDetail?.propertyId?.propertyType}</ValueText>
            </ButtonsSection>
            <ButtonsSection>
              <LabelText>Furnished Type</LabelText>
              <ValueText>{bookingDetail?.propertyId?.furnishedType}</ValueText>
            </ButtonsSection>
            <ButtonsSection>
              <LabelText size="h5">Rental Market</LabelText>
              <ValueText>{bookingDetail?.propertyId?.rentalMarket}</ValueText>
            </ButtonsSection>
            <ButtonsSection>
              <LabelText>Description</LabelText>
              <ValueText>{bookingDetail?.propertyId?.description}</ValueText>
            </ButtonsSection>
            <ButtonsSection>
              <LabelText>Booking Status</LabelText>
              <ValueText>
                {renderstatus(bookingDetail?.bookingStatus)}
              </ValueText>
            </ButtonsSection>
            <ButtonsSection>
              <LabelText>LandLord</LabelText>
              <ValueText>
                {renderFullDetails({
                  avatar: bookingDetail?.landlordId?.avatar,
                  firstName: bookingDetail?.landlordId?.firstName,
                  lastName: bookingDetail?.landlordId?.lastName,
                  email: bookingDetail?.landlordId?.email,
                })}
              </ValueText>
            </ButtonsSection>
          </Card>

          <LabelText>Tenants</LabelText>
          <Table
            tableData={tableData}
            noData="No data to display."
            headingBgColor="#f0f0f0"
            showMenuIcon={false}
            evenRowBgColor="#fafafa"
            isLoading={loading}
          />
          <DeleteModal
            title="Are you sure want to delete this booking?"
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onDelete={() => bookingId && handleDeleteBooking(bookingId)}
            itemName={bookingDetail?.propertyId?.address || ""}
            loading={false}
          />
        </>
      )}
    </Wrapper>
  );
};

export default BookingDetails;
